
import { Card, CardContent } from "@/components/ui/card";
import { Clock, GraduationCap, Trophy, Wrench, Heart, Users } from "lucide-react";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import gptwCertification from "@/assets/gptw-certification.png";
import rseCertification from "@/assets/rse-certification.png";
import lesEchosCertification from "@/assets/les-echos-certification.png";
import YouTubeLite from "./YouTubeLite";

const AdvantagesSection = () => {
  const { elementRef: titleRef, isVisible: titleVisible } = useIntersectionObserver();
  const { elementRef: cardsRef, isVisible: cardsVisible } = useIntersectionObserver();
  const { elementRef: statsRef, isVisible: statsVisible } = useIntersectionObserver();
  const { elementRef: certificationsRef, isVisible: certificationsVisible } = useIntersectionObserver();
  const { elementRef: videoRef, isVisible: videoVisible } = useIntersectionObserver();

  const advantages = [
    {
      icon: Clock,
      title: "Liberté totale",
      description: "À votre rythme, depuis chez vous, sans obligation de résultats et parfaitement cumulable avec votre activité actuelle."
    },
    {
      icon: GraduationCap,
      title: "Formation complète",
      description: "Vous êtes formé dès le départ et accompagné pendant 1 an"
    },
    {
      icon: Trophy,
      title: "Notoriété reconnue",
      description: "Vous profitez de la notoriété d'un cabinet classé Top 50 en France (source : Les Échos)"
    },
    {
      icon: Wrench,
      title: "Outils performants",
      description: "Vous accédez à une méthodologie éprouvée, des outils digitaux puissants (Nicoka, IA, Talent to Be…)"
    },
    {
      icon: Heart,
      title: "Culture bienveillante",
      description: "Vous bénéficiez d'une culture bienveillante, collaborative, humaine"
    },
    {
      icon: Users,
      title: "Masterclass hebdomadaires",
      description: "Chaque semaine, participez à des masterclass sur le recrutement pour développer vos compétences et votre expertise"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-white to-pink-50 relative overflow-hidden">
      {/* Organic background shapes */}
      <div className="absolute inset-0">
        <div className="absolute top-10 right-10 w-96 h-96 bg-primary/10 blob-shape animate-float opacity-30"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-secondary/10 blob-shape animate-float opacity-40" style={{animationDelay: '3s'}}></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-800 ${
            titleVisible ? 'animate-slide-in-up' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Pourquoi devenir indépendant chez <span className="gradient-text">Work&You</span> ?
          </h2>
        </div>

        <div 
          ref={cardsRef}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
        >
          {advantages.map((advantage, index) => (
            <Card 
              key={index} 
              className={`group hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:-translate-y-2 bg-white/80 backdrop-blur-sm ${
                cardsVisible 
                  ? `animate-slide-in-up animate-stagger-${(index % 6) + 1}` 
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <CardContent className="p-8 h-full">
                <div className="flex flex-col h-full">
                  <div className="w-16 h-16 rounded-2xl gradient-bg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <advantage.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">✔️</span>
                    <h3 className="text-xl font-bold text-gray-900">{advantage.title}</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed flex-grow">{advantage.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div 
          ref={statsRef}
          className={`gradient-bg rounded-3xl p-8 md:p-12 text-center shadow-2xl relative overflow-hidden transition-all duration-800 ${
            statsVisible ? 'animate-scale-in' : 'opacity-0 scale-95'
          }`}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 blob-shape"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/20 blob-shape"></div>
          
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <div className="bg-white/90 rounded-2xl p-6 shadow-xl backdrop-blur-sm">
                  <div className="text-4xl font-bold text-primary mb-2">92%</div>
                  <div className="text-gray-700 font-medium">de maintien dans le réseau après 1 an</div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-white/90 rounded-2xl p-6 shadow-xl backdrop-blur-sm">
                  <div className="text-4xl font-bold text-secondary mb-2">9,4/10</div>
                  <div className="text-gray-700 font-medium">Indice de satisfaction (évaluations internes 2024)</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div 
          ref={certificationsRef}
          className={`mt-16 max-w-6xl mx-auto transition-all duration-800 ${
            certificationsVisible ? 'animate-scale-in' : 'opacity-0 scale-95'
          }`}
        >
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Nos <span className="gradient-text">certifications</span>
            </h3>
            <p className="text-lg text-gray-600">
              Des reconnaissances qui témoignent de notre engagement et de notre qualité de service
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group">
              <Card className="h-full hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:-translate-y-2 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <div className="h-32 flex items-center justify-center mb-6">
                    <img 
                      src={lesEchosCertification} 
                      alt="Les Échos Top 50"
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      width="178"
                      height="215"
                      loading="lazy"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Les Échos Top 50</h4>
                  <p className="text-gray-600">
                    Classé parmi les 50 meilleurs cabinets de recrutement de France
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="group">
              <Card className="h-full hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:-translate-y-2 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <div className="h-32 flex items-center justify-center mb-6">
                    <img 
                      src={gptwCertification} 
                      alt="Great Place to Work"
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      width="1127"
                      height="1920"
                      loading="lazy"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Great Place to Work</h4>
                  <p className="text-gray-600">
                    Certification pour la satisfaction et le bien-être de nos collaborateurs
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="group">
              <Card className="h-full hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:-translate-y-2 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <div className="h-32 flex items-center justify-center mb-6">
                    <img 
                      src={rseCertification} 
                      alt="Engagé RSE"
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      width="200"
                      height="198"
                      loading="lazy"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Engagé RSE</h4>
                  <p className="text-gray-600">
                    Notre engagement environnemental et social responsable
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <div 
          id="video-presentation" 
          ref={videoRef}
          className={`mt-16 max-w-4xl mx-auto transition-all duration-800 ${
            videoVisible ? 'animate-slide-in-up' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-8">
            <h3 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Découvrez <span className="gradient-text">Work&You</span>
            </h3>
            <p className="text-lg text-gray-600">
              Présentation du groupe et de notre approche unique du recrutement
            </p>
          </div>
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            <div className="aspect-video">
              <YouTubeLite 
                videoId="yK00B6hxKMo"
                title="Témoignages Work&You"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSection;
